// frontend/app/components/HomeworkHelpPanel.js
// BDD 02/23/2026 HomeWorkHelpV2-A
import React from "react";
import { View, Text, ScrollView, Image, TouchableOpacity, Platform, TextInput } from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import MarkdownRenderer from "./MarkdownRenderer";
import { getMainContentColors } from "./MainContentArea";

// --- helpers ---
function stripTrailingSlash(s) {
  return (s || "").replace(/\/+$/, "");
}
function qs(params) {
  const usp = new URLSearchParams();
  Object.entries(params || {}).forEach(([k, v]) => {
    if (v === undefined || v === null || v === "") return;
    usp.append(k, String(v));
  });
  return usp.toString();
}
async function fetchJson(url, opts) {
  const resp = await fetch(url, opts);
  const text = await resp.text();
  let data = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = { raw: text };
  }
  if (!resp.ok) {
    const msg = (data && (data.detail || data.error)) || `HTTP ${resp.status}`;
    throw new Error(msg);
  }
  return data;
}
function sleep(ms) {
  return new Promise((r) => setTimeout(r, ms));
}

function storageKey(userId, contentDb) {
  return `hw_assignment_id::${String(userId || "")}::${String(contentDb || "")}`;
}
function loadStoredAssignmentId(userId, contentDb) {
  try {
    if (typeof window !== "undefined" && window.sessionStorage) {
      const v = window.sessionStorage.getItem(storageKey(userId, contentDb));
      if (!v) return null;
      const n = Number(v);
      return Number.isFinite(n) ? n : null;
    }
  } catch {}
  return null;
}
function saveStoredAssignmentId(userId, contentDb, assignmentId) {
  try {
    if (typeof window !== "undefined" && window.sessionStorage) {
      if (assignmentId == null) {
        window.sessionStorage.removeItem(storageKey(userId, contentDb));
      } else {
        window.sessionStorage.setItem(storageKey(userId, contentDb), String(assignmentId));
      }
    }
  } catch {}
}

export default function HomeworkHelpPanel({
  apiBaseUrl,
  userId,
  contentDb,
  homeworkModeOn,
  onEnableHomeworkMode,
  onAskProblem,
  t = (x) => x,
  isDarkMode = true,
}) {
  const base = React.useMemo(() => stripTrailingSlash(apiBaseUrl), [apiBaseUrl]);

  const [assignmentId, setAssignmentId] = React.useState(null);
  const [assignments, setAssignments] = React.useState([]);
  const [documents, setDocuments] = React.useState([]); // [{document_id,status,page_count,pages_ready,last_error}]
  const [problems, setProblems] = React.useState([]);   // server shape
  const [pages, setPages] = React.useState([]);         // [{page_no,url}]
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState(null);

  const fileInputRef = React.useRef(null);

  const colors = getMainContentColors(isDarkMode);
  const cardBg = colors.cardBg;
  const border = colors.border;
  const textColor = colors.textPrimary;
  const subText = colors.textSecondary;
  // Saras blue palette instead of indigo/purple
  const sarasBlue = "#4A90E2";
  const sarasBlueBg = isDarkMode ? "rgba(74,144,226,0.12)" : "rgba(74,144,226,0.08)";
  const sarasBlueBorder = isDarkMode ? "rgba(74,144,226,0.3)" : "rgba(74,144,226,0.3)";
  const sarasBlueText = isDarkMode ? "#6bb3f7" : "#4A90E2";
  const greenBg = isDarkMode ? "rgba(34,197,94,0.15)" : "rgba(34,197,94,0.10)";
  const greenBorder = isDarkMode ? "rgba(34,197,94,0.35)" : "rgba(34,197,94,0.40)";
  const greenText = isDarkMode ? "#4ade80" : "#15803d";

  const logPrefix = "[HW_PANEL]";

  const refreshAssignments = React.useCallback(async () => {
    if (!base || !userId) return [];
    setError(null);
    const url = `${base}/homework/assignments?${qs({ user_id: userId, content_db: contentDb, limit: 30 })}`;
    console.log(logPrefix, "GET assignments", url);
    const data = await fetchJson(url);
    const list = Array.isArray(data?.assignments) ? data.assignments : [];
    setAssignments(list);
    return list;
  }, [base, userId, contentDb]);

  const createAssignment = React.useCallback(async () => {
    if (!base || !userId) return null;
    setError(null);
    const assignment_date = new Date().toISOString().slice(0, 10);

    const url = `${base}/homework/assignments?${qs({
      user_id: userId,
      content_db: contentDb,
      title: `Homework ${assignment_date}`,
      assignment_date,
    })}`;
    console.log(logPrefix, "POST create_assignment", url);
    const data = await fetchJson(url, { method: "POST" });
    const id = data?.assignment_id ?? null;
    return id ? Number(id) : null;
  }, [base, userId, contentDb]);

  const refreshProblemsAndPages = React.useCallback(async (aid) => {
    const assignment_id = aid || assignmentId;
    if (!base || !userId || !assignment_id) return;

    setError(null);

    const pUrl = `${base}/homework/assignments/${assignment_id}/problems?${qs({ user_id: userId })}`;
    console.log(logPrefix, "GET problems", pUrl);
    const pData = await fetchJson(pUrl);
    setProblems(Array.isArray(pData?.problems) ? pData.problems : []);

    const imgUrl = `${base}/homework/assignments/${assignment_id}/page_images?${qs({
      user_id: userId,
      expires_seconds: 900,
    })}`;
    console.log(logPrefix, "GET page_images", imgUrl);
    const imgData = await fetchJson(imgUrl);
    setPages(Array.isArray(imgData?.pages) ? imgData.pages : []);
  }, [base, userId, assignmentId]);

    // BDD 02/23/2026 - Homeworkhelpv2
    const [titleDraft, setTitleDraft] = React.useState("");
    const [savingTitle, setSavingTitle] = React.useState(false);

        // BDD 02/23/2026
    const updateAssignmentTitle = React.useCallback(async (aid, newTitle) => {
    if (!base || !userId || !aid) return;
    const url = `${base}/homework/assignments/${aid}?${qs({
        user_id: userId,
        title: newTitle,
    })}`;
    console.log(logPrefix, "PATCH update_assignment", url);
    const data = await fetchJson(url, { method: "PATCH" });

    // update local list so dropdown updates immediately
    setAssignments((prev) =>
        (prev || []).map((x) => {
        const xid = x?.assignment_id ?? x?.id;
        return String(xid) === String(aid) ? { ...x, ...data } : x;
        })
    );

    return data;
    }, [base, userId]);

    // BDD 02/23/2026 - Homeworkhelpv2
    React.useEffect(() => {
        if (!assignmentId) return;
        const a = (assignments || []).find((x) => String(x.assignment_id ?? x.id) === String(assignmentId));
        const t = (a?.title || "").toString();
        setTitleDraft(t);
    }, [assignmentId, assignments]);

  const pollDocument = React.useCallback(async (document_id, aid) => {
    const assignment_id = aid || assignmentId;
    if (!base || !userId || !document_id) return;

    for (let i = 0; i < 90; i++) {
      const sUrl = `${base}/homework/documents/${document_id}?${qs({ user_id: userId })}`;
      console.log(logPrefix, "GET document_status", sUrl);
      const d = await fetchJson(sUrl);

      setDocuments((prev) => {
        const next = [...(prev || [])];
        const idx = next.findIndex((x) => String(x?.document_id) === String(document_id));
        const row = {
          document_id: d?.document_id ?? document_id,
          assignment_id: d?.assignment_id ?? assignment_id,
          status: d?.status ?? "unknown",
          page_count: d?.page_count ?? null,
          pages_ready: d?.pages_ready ?? 0,
          problems_extracted: d?.problems_extracted ?? 0,
          last_error: d?.last_error ?? null,
        };
        if (idx >= 0) next[idx] = row;
        else next.unshift(row);
        return next;
      });

      const st = String(d?.status || "").toLowerCase();
      if (st === "processed" || st === "ready" || st === "done") {
        await refreshProblemsAndPages(assignment_id);
        return;
      }
      if (st === "failed" || st === "error") return;

      await sleep(1000);
    }
  }, [base, userId, assignmentId, refreshProblemsAndPages]);

  const selectAssignment = React.useCallback(async (id) => {
    const n = id != null ? Number(id) : null;
    if (!n || !Number.isFinite(n)) return;

    setAssignmentId(n);
    saveStoredAssignmentId(userId, contentDb, n);

    // reset per-assignment UI state
    setDocuments([]);
    setProblems([]);
    setPages([]);

    await refreshProblemsAndPages(n);
  }, [userId, contentDb, refreshProblemsAndPages]);

  const ensureAssignment = React.useCallback(async () => {
    setBusy(true);
    try {
      const list = await refreshAssignments();

      // 1) Try stored selection
      const storedId = loadStoredAssignmentId(userId, contentDb);
      if (storedId && Array.isArray(list) && list.some((x) => Number(x.assignment_id || x.id) === storedId)) {
        await selectAssignment(storedId);
        return storedId;
      }

      // 2) Otherwise pick most recent
      const latest = Array.isArray(list) && list.length ? list[0] : null;
      const latestId = latest ? Number(latest.assignment_id ?? latest.id ?? null) : null;
      if (latestId) {
        await selectAssignment(latestId);
        return latestId;
      }

      // 3) Otherwise create
      const newId = await createAssignment();
      if (newId) {
        await refreshAssignments();
        await selectAssignment(newId);
      }
      return newId;
    } finally {
      setBusy(false);
    }
  }, [refreshAssignments, createAssignment, selectAssignment, userId, contentDb]);

  React.useEffect(() => {
    if (!base || !userId) return;
    ensureAssignment().catch((e) => {
      console.warn(logPrefix, "ensureAssignment failed:", e);
      setError(String(e?.message || e));
    });
  }, [base, userId, contentDb]); // re-init if contentDb changes

  const handlePickFile = React.useCallback(() => {
    if (Platform.OS !== "web") {
      setError(t("Upload is available on web for now. Mobile upload is coming soon."));
      return;
    }
    fileInputRef.current?.click?.();
  }, []);

  const uploadFile = React.useCallback(async (file) => {
    if (!base || !userId) return;
    setError(null);

    let aid = assignmentId;
    if (!aid) {
      aid = await ensureAssignment();
      if (!aid) throw new Error("No assignment_id available");
    }

    const url = `${base}/homework/assignments/${aid}/upload`;
    console.log(logPrefix, "POST upload", url, { name: file?.name, type: file?.type, size: file?.size });

    const form = new FormData();
    form.append("user_id", userId);
    form.append("content_db", contentDb);
    form.append("file", file);

    setBusy(true);
    try {
      const resp = await fetch(url, { method: "POST", body: form });
      const text = await resp.text();
      let data = null;
      try { data = text ? JSON.parse(text) : null; } catch { data = { raw: text }; }
      if (!resp.ok) {
        const msg = (data && (data.detail || data.error)) || `HTTP ${resp.status}`;
        throw new Error(msg);
      }

      const document_id = data?.document_id;
      if (!document_id) throw new Error("Upload succeeded but no document_id returned");

      await pollDocument(document_id, aid);
    } finally {
      setBusy(false);
    }
  }, [base, userId, contentDb, assignmentId, ensureAssignment, pollDocument]);

  const onWebFileChange = React.useCallback(async (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;
    e.target.value = "";
    try {
      await uploadFile(file);
    } catch (err) {
      console.warn(logPrefix, "uploadFile failed:", err);
      setError(String(err?.message || err));
    }
  }, [uploadFile]);

  const headerTitle = t("Homework Help");

  return (
    <View style={{
      backgroundColor: cardBg,
      borderWidth: 1,
      borderColor: border,
      borderRadius: 16,
      padding: 16,
      marginBottom: 12,
      shadowColor: colors.shadow,
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: colors.shadowOpacity,
      shadowRadius: 12,
      elevation: 4,
      overflow: "hidden",
    }}>
      {/* Header row */}
      <View style={{ flexDirection: "row", alignItems: "center", justifyContent: "space-between" }}>
        <View style={{ flexDirection: "row", alignItems: "center", gap: 10 }}>
          <View style={{
            width: 36,
            height: 36,
            borderRadius: 10,
            backgroundColor: sarasBlueBg,
            alignItems: "center",
            justifyContent: "center",
          }}>
            <MaterialCommunityIcons name="school-outline" size={20} color={sarasBlue} />
          </View>
          <View>
            <Text style={{ color: textColor, fontSize: 16, fontWeight: "700" }}>
              {headerTitle}
            </Text>
            {assignmentId != null && (
              <View style={{ flexDirection: "row", alignItems: "center", marginTop: 2 }}>
                <View style={{
                  backgroundColor: sarasBlueBg,
                  paddingVertical: 2,
                  paddingHorizontal: 8,
                  borderRadius: 10,
                }}>
                  <Text style={{ color: sarasBlue, fontSize: 11, fontWeight: "600" }}>
                    ID {assignmentId}
                  </Text>
                </View>
                <Text style={{ color: subText, fontSize: 12, marginLeft: 8 }}>
                  {contentDb}
                </Text>
              </View>
            )}
          </View>
        </View>

        <View style={{ flexDirection: "row", gap: 8 }}>
          {!homeworkModeOn && (
            <TouchableOpacity
              onPress={() => onEnableHomeworkMode?.()}
              style={{
                paddingVertical: 8,
                paddingHorizontal: 14,
                borderRadius: 10,
                backgroundColor: greenBg,
                borderWidth: 1,
                borderColor: greenBorder,
                flexDirection: "row",
                alignItems: "center",
                gap: 6,
              }}
              disabled={busy}
            >
              <MaterialCommunityIcons name="power" size={16} color={greenText} />
              <Text style={{ color: greenText, fontWeight: "700", fontSize: 13 }}>{t("Turn ON")}</Text>
            </TouchableOpacity>
          )}

          <TouchableOpacity
            onPress={() => refreshProblemsAndPages().catch((e) => setError(String(e?.message || e)))}
            style={{
              paddingVertical: 8,
              paddingHorizontal: 12,
              borderRadius: 10,
              backgroundColor: isDarkMode ? "rgba(148,163,184,0.08)" : "rgba(148,163,184,0.06)",
              borderWidth: 1,
              borderColor: border,
              flexDirection: "row",
              alignItems: "center",
              gap: 6,
            }}
            disabled={busy}
          >
            <MaterialCommunityIcons name="sync" size={16} color={subText} />
            <Text style={{ color: subText, fontWeight: "600", fontSize: 13 }}>{t("Refresh")}</Text>
          </TouchableOpacity>
        </View>
      </View>

      {error ? (
        <Text style={{ color: "#ff6b6b", marginTop: 8, fontWeight: "600" }}>
          {t("Error")}: {error}
        </Text>
      ) : null}

      {/* Action toolbar */}
      <View style={{
        flexDirection: "row",
        alignItems: "center",
        gap: 8,
        marginTop: 14,
        paddingTop: 14,
        borderTopWidth: 1,
        borderTopColor: border,
        flexWrap: "wrap",
      }}>
        <TouchableOpacity
          onPress={async () => {
            try {
              setBusy(true);
              setError(null);
              const newId = await createAssignment();
              if (newId) {
                await refreshAssignments();
                await selectAssignment(newId);
              }
            } catch (e) {
              setError(String(e?.message || e));
            } finally {
              setBusy(false);
            }
          }}
          style={{
            paddingVertical: 8,
            paddingHorizontal: 14,
            borderRadius: 10,
            backgroundColor: isDarkMode ? "rgba(148,163,184,0.08)" : "rgba(148,163,184,0.06)",
            borderWidth: 1,
            borderColor: border,
            flexDirection: "row",
            alignItems: "center",
            gap: 6,
          }}
          disabled={busy}
        >
          <MaterialCommunityIcons name="plus-circle-outline" size={16} color={subText} />
          <Text style={{ color: textColor, fontWeight: "600", fontSize: 13 }}>
            {t("New Assignment")}
          </Text>
        </TouchableOpacity>

        {/* Web-only dropdown to load older assignments */}
        {Platform.OS === "web" && assignments.length > 0 && (
          <View style={{
            borderWidth: 1,
            borderColor: border,
            borderRadius: 10,
            overflow: "hidden",
            backgroundColor: isDarkMode ? "rgba(148,163,184,0.08)" : "rgba(148,163,184,0.06)",
          }}>
            <select
              value={assignmentId ?? ""}
              onChange={(e) => {
                const v = Number(e.target.value);
                if (Number.isFinite(v)) {
                  selectAssignment(v).catch((err) => setError(String(err?.message || err)));
                }
              }}
              style={{
                padding: "8px 12px",
                background: "transparent",
                color: isDarkMode ? "#e2e8f0" : "#0f172a",
                border: "none",
                outline: "none",
                minWidth: 240,
                fontSize: 13,
                cursor: "pointer",
              }}
              disabled={busy}
            >
              {assignments.map((a) => {
                const id = Number(a.assignment_id ?? a.id);
                const label = a.title || t("Assignment {id}", { id });
                const date = a.assignment_date ? ` \u2022 ${a.assignment_date}` : "";
                return (
                  <option key={String(id)} value={String(id)} style={{ backgroundColor: isDarkMode ? "#1e293b" : "#ffffff", color: isDarkMode ? "#e2e8f0" : "#0f172a" }}>
                    {label}{date}
                  </option>
                );
              })}
            </select>
          </View>
        )}

        <TouchableOpacity
          onPress={handlePickFile}
          style={{
            paddingVertical: 8,
            paddingHorizontal: 14,
            borderRadius: 10,
            backgroundColor: sarasBlue,
            flexDirection: "row",
            alignItems: "center",
            gap: 6,
            shadowColor: sarasBlue,
            shadowOffset: { width: 0, height: 2 },
            shadowOpacity: 0.3,
            shadowRadius: 6,
            elevation: 3,
            opacity: (busy || !assignmentId) ? 0.5 : 1,
          }}
          disabled={busy || !assignmentId}
        >
          <MaterialCommunityIcons name="cloud-upload-outline" size={16} color="#fff" />
          <Text style={{ color: "#fff", fontWeight: "700", fontSize: 13 }}>
            {busy ? t("Working\u2026") : t("Upload PDF / Image")}
          </Text>
        </TouchableOpacity>

        {Platform.OS === "web" && (
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf,image/*"
            style={{ display: "none" }}
            onChange={onWebFileChange}
          />
        )}
      </View>

      {/* Homework name */}
      <View style={{ marginTop: 14 }}>
        <Text style={{ color: subText, marginBottom: 6, fontSize: 12, fontWeight: "600", textTransform: "uppercase", letterSpacing: 0.5 }}>
          {t("Homework name")}
        </Text>

        <View style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
          <TextInput
            value={titleDraft}
            onChangeText={setTitleDraft}
            placeholder={t("e.g., Science - Matter worksheet")}
            placeholderTextColor={isDarkMode ? "rgba(226,232,240,0.35)" : "rgba(15,23,42,0.35)"}
            style={{
              flex: 1,
              paddingVertical: 10,
              paddingHorizontal: 14,
              borderRadius: 10,
              borderWidth: 1,
              borderColor: border,
              color: textColor,
              backgroundColor: isDarkMode ? "rgba(0,0,0,0.15)" : "rgba(0,0,0,0.03)",
              fontSize: 14,
            }}
          />

          <TouchableOpacity
            disabled={busy || savingTitle || !assignmentId}
            onPress={async () => {
              try {
                setSavingTitle(true);
                await updateAssignmentTitle(assignmentId, titleDraft || "");
                await refreshAssignments();
              } catch (e) {
                setError(String(e?.message || e));
              } finally {
                setSavingTitle(false);
              }
            }}
            style={{
              paddingVertical: 10,
              paddingHorizontal: 16,
              borderRadius: 10,
              backgroundColor: sarasBlueBg,
              borderWidth: 1,
              borderColor: sarasBlueBorder,
              opacity: (busy || savingTitle || !assignmentId) ? 0.5 : 1,
              flexDirection: "row",
              alignItems: "center",
              gap: 4,
            }}
          >
            <MaterialCommunityIcons name="content-save-outline" size={15} color={sarasBlueText} />
            <Text style={{ color: sarasBlueText, fontWeight: "700", fontSize: 13 }}>
              {savingTitle ? t("Saving\u2026") : t("Save")}
            </Text>
          </TouchableOpacity>
        </View>
      </View>

      <View style={{ marginTop: 16 }}>
        <Text style={{ color: textColor, fontWeight: "700", marginBottom: 8, fontSize: 14 }}>
          {t("Uploads")}
        </Text>

        {documents.length === 0 ? (
          <View style={{
            borderWidth: 1.5,
            borderColor: border,
            borderStyle: "dashed",
            borderRadius: 12,
            paddingVertical: 24,
            paddingHorizontal: 16,
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: isDarkMode ? "rgba(0,0,0,0.08)" : "rgba(0,0,0,0.02)",
          }}>
            <MaterialCommunityIcons name="file-upload-outline" size={28} color={colors.textMuted} />
            <Text style={{ color: subText, marginTop: 8, textAlign: "center", fontSize: 13 }}>
              {t("No uploads yet. Upload a PDF or image to extract questions.")}
            </Text>
          </View>
        ) : (
          documents.slice(0, 8).map((d) => {
            const st = String(d.status || "").toLowerCase();
            const statusColor = st === "processed" || st === "ready" || st === "done"
              ? { bg: greenBg, text: greenText, border: greenBorder }
              : st === "failed" || st === "error"
              ? { bg: isDarkMode ? "rgba(239,68,68,0.15)" : "rgba(239,68,68,0.08)", text: "#ef4444", border: "rgba(239,68,68,0.3)" }
              : { bg: isDarkMode ? "rgba(234,179,8,0.15)" : "rgba(234,179,8,0.08)", text: isDarkMode ? "#facc15" : "#a16207", border: "rgba(234,179,8,0.3)" };

            return (
              <View
                key={String(d.document_id)}
                style={{
                  paddingVertical: 10,
                  paddingHorizontal: 12,
                  borderRadius: 10,
                  borderWidth: 1,
                  borderColor: border,
                  backgroundColor: isDarkMode ? "rgba(0,0,0,0.12)" : "rgba(0,0,0,0.02)",
                  marginTop: 6,
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <View style={{ flex: 1 }}>
                  <View style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
                    <MaterialCommunityIcons name="file-document-outline" size={16} color={subText} />
                    <Text style={{ color: textColor, fontWeight: "600", fontSize: 13 }}>
                      Doc #{d.document_id}
                    </Text>
                    <View style={{
                      backgroundColor: statusColor.bg,
                      borderWidth: 1,
                      borderColor: statusColor.border,
                      paddingVertical: 2,
                      paddingHorizontal: 8,
                      borderRadius: 10,
                    }}>
                      <Text style={{ color: statusColor.text, fontSize: 11, fontWeight: "600" }}>
                        {String(d.status || "unknown").toUpperCase()}
                      </Text>
                    </View>
                  </View>
                  <Text style={{ color: subText, fontSize: 12, marginTop: 4 }}>
                    {d.pages_ready ?? 0}/{d.page_count ?? "?"} pages \u2022 {d.problems_extracted ?? 0} problems
                  </Text>
                </View>
                {d.last_error ? (
                  <Text style={{ color: "#ef4444", fontSize: 12, marginLeft: 8, maxWidth: 200 }} numberOfLines={2}>
                    {d.last_error}
                  </Text>
                ) : null}
              </View>
            );
          })
        )}
      </View>

      {/* Problems */}
      <View style={{ marginTop: 16 }}>
        <Text style={{ color: textColor, fontWeight: "700", marginBottom: 8, fontSize: 14 }}>
          {t("Extracted Problems")}
        </Text>

        {problems.length === 0 ? (
          <View style={{
            paddingVertical: 20,
            paddingHorizontal: 16,
            alignItems: "center",
          }}>
            <MaterialCommunityIcons name="text-box-search-outline" size={28} color={colors.textMuted} />
            <Text style={{ color: subText, marginTop: 8, textAlign: "center", fontSize: 13 }}>
              {t("No problems extracted yet. Upload first, then wait for processing.")}
            </Text>
          </View>
        ) : (
          <View style={{ gap: 10 }}>
            {problems.slice(0, 40).map((p, idx) => {
              const statement =
                (p?.statement_md ||
                  p?.statement_md_final ||
                  p?.statement_md_raw ||
                  "").toString().trim();

              const ordinal = p?.ordinal ?? p?.problem_index ?? (idx + 1);
              const key = String(p?.problem_id ?? p?.id ?? idx);

              return (
                <View
                  key={key}
                  style={{
                    borderWidth: 1,
                    borderColor: border,
                    borderRadius: 12,
                    padding: 14,
                    backgroundColor: isDarkMode ? "rgba(0,0,0,0.12)" : "rgba(0,0,0,0.02)",
                  }}
                >
                  <View style={{ flexDirection: "row", alignItems: "center", gap: 10 }}>
                    <View style={{
                      width: 28,
                      height: 28,
                      borderRadius: 14,
                      backgroundColor: sarasBlue,
                      alignItems: "center",
                      justifyContent: "center",
                    }}>
                      <Text style={{ color: "#fff", fontWeight: "700", fontSize: 13 }}>
                        {ordinal}
                      </Text>
                    </View>
                    <Text style={{ color: subText, fontSize: 12, fontWeight: "600" }}>
                      Problem {ordinal}
                    </Text>
                  </View>

                  <View style={{ marginTop: 10, marginLeft: 2 }}>
                    <MarkdownRenderer content={statement || "\u2014"} />
                  </View>

                  <View style={{ marginTop: 12 }}>
                    <TouchableOpacity
                      onPress={() => onAskProblem?.(p, assignmentId)}
                      activeOpacity={0.75}
                      style={{
                        paddingVertical: 9,
                        paddingHorizontal: 18,
                        borderRadius: 10,
                        backgroundColor: sarasBlueBg,
                        borderWidth: 1,
                        borderColor: sarasBlueBorder,
                        flexDirection: "row",
                        alignItems: "center",
                        alignSelf: "flex-start",
                        gap: 7,
                        shadowColor: sarasBlue,
                        shadowOffset: { width: 0, height: 1 },
                        shadowOpacity: 0.15,
                        shadowRadius: 4,
                        elevation: 2,
                      }}
                    >
                      <MaterialCommunityIcons name="chat-processing-outline" size={16} color={sarasBlueText} />
                      <Text style={{ color: sarasBlueText, fontWeight: "700", fontSize: 13 }}>
                        {t("Ask Saras AI")}
                      </Text>
                    </TouchableOpacity>
                  </View>
                </View>
              );
            })}
          </View>
        )}
      </View>

      {/* Page previews */}
      {pages.length > 0 && (
        <View style={{ marginTop: 16 }}>
          <Text style={{ color: textColor, fontWeight: "700", marginBottom: 8, fontSize: 14 }}>
            {t("Page Previews")}
          </Text>

          <ScrollView horizontal style={{ flexGrow: 0 }} showsHorizontalScrollIndicator={false}>
            <View style={{ flexDirection: "row", gap: 10 }}>
              {pages.slice(0, 12).map((pg, i) => (
                <View key={String(pg.page_no ?? pg.page_index ?? i)} style={{ width: 160, alignItems: "center" }}>
                  {pg.url ? (
                    <Image
                      source={{ uri: pg.url }}
                      style={{
                        width: 160,
                        height: 220,
                        borderRadius: 10,
                        backgroundColor: isDarkMode ? "#1e293b" : "#e2e8f0",
                        borderWidth: 1,
                        borderColor: border,
                        shadowColor: colors.shadow,
                        shadowOffset: { width: 0, height: 2 },
                        shadowOpacity: colors.shadowOpacity,
                        shadowRadius: 6,
                      }}
                      resizeMode="cover"
                    />
                  ) : (
                    <View style={{
                      width: 160,
                      height: 220,
                      borderRadius: 10,
                      backgroundColor: isDarkMode ? "#1e293b" : "#e2e8f0",
                      borderWidth: 1,
                      borderColor: border,
                    }} />
                  )}
                  <Text style={{ color: subText, marginTop: 6, fontSize: 12, fontWeight: "500", textAlign: "center" }}>
                    Page {pg.page_no ?? pg.page_index ?? (i + 1)}
                  </Text>
                </View>
              ))}
            </View>
          </ScrollView>
        </View>
      )}
    </View>
  );
}
